/**
 * SettingsPanel - Modal panel for audio and haptics settings
 * Provides toggles for music, SFX, and haptics with persistence.
 */

import Phaser from 'phaser';
import { AudioManager } from '../systems/AudioManager';
import { HapticsManager } from '../systems/HapticsManager';
import { COLORS } from '../config/gameConfig';

/**
 * Settings panel configuration
 */
export interface SettingsPanelConfig {
  /** Panel width */
  width: number;
  /** Panel height */
  height: number;
  /** Background color */
  backgroundColor: number;
  /** Background alpha */
  backgroundAlpha: number;
}

const DEFAULT_CONFIG: SettingsPanelConfig = {
  width: 280,
  height: 240,
  backgroundColor: COLORS.BACKGROUND,
  backgroundAlpha: 0.98,
};

/**
 * Modal settings panel for audio and haptics controls
 */
export class SettingsPanel {
  private scene: Phaser.Scene;
  private audioManager: AudioManager;
  private haptics: HapticsManager;
  private config: SettingsPanelConfig;

  private container!: Phaser.GameObjects.Container;
  private overlay!: Phaser.GameObjects.Rectangle;
  private isVisible: boolean = false;
  private toggles: Map<string, { bg: Phaser.GameObjects.Rectangle; knob: Phaser.GameObjects.Arc }> =
    new Map();

  constructor(
    scene: Phaser.Scene,
    audioManager: AudioManager,
    haptics: HapticsManager,
    config?: Partial<SettingsPanelConfig>
  ) {
    this.scene = scene;
    this.audioManager = audioManager;
    this.haptics = haptics;
    this.config = { ...DEFAULT_CONFIG, ...config };

    this.create();
  }

  /**
   * Create the settings panel UI
   */
  private create(): void {
    const { width, height } = this.scene.scale;
    const centerX = width / 2;
    const centerY = height / 2;

    // Dimmed overlay background
    this.overlay = this.scene.add.rectangle(centerX, centerY, width, height, 0x000000, 0.5);
    this.overlay.setInteractive();
    this.overlay.on('pointerup', () => this.hide());
    this.overlay.setDepth(199);
    this.overlay.setVisible(false);

    // Create container for panel content
    this.container = this.scene.add.container(centerX, centerY);
    this.container.setDepth(200);
    this.container.setVisible(false);

    // Panel background with shadow
    const shadow = this.scene.add.rectangle(4, 4, this.config.width, this.config.height, 0x000000, 0.2);
    shadow.setStrokeStyle(0);

    const bg = this.scene.add.rectangle(
      0,
      0,
      this.config.width,
      this.config.height,
      this.config.backgroundColor,
      this.config.backgroundAlpha
    );
    bg.setStrokeStyle(2, COLORS.PLATFORM);

    // Title
    const title = this.scene.add.text(0, -85, 'SETTINGS', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '24px',
      color: '#2D3436',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);

    // Create toggle rows (Sound FX and Haptics only - no ambient music)
    const sfxRow = this.createToggleRow(-25, 'Sound FX', this.audioManager.getSFXVolume() > 0, (value) => {
      this.audioManager.setSFXVolume(value ? 0.6 : 0);
    });

    const hapticsRow = this.createToggleRow(35, 'Haptics', this.haptics.isEnabled(), (value) => {
      this.haptics.setEnabled(value);
    });

    // Close button
    const closeBtn = this.scene.add.text(0, 90, 'CLOSE', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '20px',
      color: '#7C9885',
      fontStyle: 'bold',
    });
    closeBtn.setOrigin(0.5);
    closeBtn.setInteractive({ useHandCursor: true });

    closeBtn.on('pointerover', () => closeBtn.setColor('#5B7B63'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#7C9885'));
    closeBtn.on('pointerup', () => {
      this.audioManager.playButtonTap();
      this.haptics.buttonTap();
      this.hide();
    });

    // Add all elements to container
    this.container.add([shadow, bg, title, ...sfxRow, ...hapticsRow, closeBtn]);
  }

  /**
   * Create a toggle row with label
   */
  private createToggleRow(
    y: number,
    label: string,
    initialValue: boolean,
    onChange: (value: boolean) => void
  ): Phaser.GameObjects.GameObject[] {
    const labelText = this.scene.add.text(-100, y, label, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '18px',
      color: '#2D3436',
    });
    labelText.setOrigin(0, 0.5);

    const toggle = this.createToggle(70, y, initialValue, onChange, label.toLowerCase());

    return [labelText, ...toggle];
  }

  /**
   * Create a toggle switch
   */
  private createToggle(
    x: number,
    y: number,
    initialValue: boolean,
    onChange: (value: boolean) => void,
    id: string
  ): Phaser.GameObjects.GameObject[] {
    let isOn = initialValue;

    // Toggle track background
    const trackWidth = 50;
    const trackHeight = 26;
    const bg = this.scene.add.rectangle(
      x,
      y,
      trackWidth,
      trackHeight,
      isOn ? 0x7c9885 : 0x9ba4b0,
      1
    );
    bg.setStrokeStyle(1, 0x2d3436, 0.3);

    // Toggle knob
    const knobRadius = 10;
    const knob = this.scene.add.circle(isOn ? x + 12 : x - 12, y, knobRadius, 0xffffff);
    knob.setStrokeStyle(1, 0x2d3436, 0.2);

    // Store references for external updates
    this.toggles.set(id, { bg, knob });

    // Interactive hit area
    const hitArea = this.scene.add.rectangle(x, y, trackWidth + 20, trackHeight + 10, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });

    hitArea.on('pointerup', () => {
      isOn = !isOn;

      // Update visuals
      bg.setFillStyle(isOn ? 0x7c9885 : 0x9ba4b0);

      this.scene.tweens.add({
        targets: knob,
        x: isOn ? x + 12 : x - 12,
        duration: 100,
        ease: 'Cubic.easeOut',
      });

      // Play feedback
      this.audioManager.playButtonTap();
      this.haptics.buttonTap();

      // Notify change
      onChange(isOn);
    });

    return [bg, knob, hitArea];
  }

  /**
   * Show the settings panel
   */
  show(): void {
    if (this.isVisible) return;

    this.isVisible = true;
    this.overlay.setVisible(true);
    this.container.setVisible(true);

    // Fade in animation
    this.overlay.setAlpha(0);
    this.container.setAlpha(0);
    this.container.setScale(0.9);

    this.scene.tweens.add({
      targets: this.overlay,
      alpha: 1,
      duration: 200,
    });

    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    this.audioManager.playButtonTap();
    this.haptics.buttonTap();
  }

  /**
   * Hide the settings panel
   */
  hide(): void {
    if (!this.isVisible) return;

    this.scene.tweens.add({
      targets: this.overlay,
      alpha: 0,
      duration: 150,
    });

    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      scaleX: 0.9,
      scaleY: 0.9,
      duration: 150,
      onComplete: () => {
        this.overlay.setVisible(false);
        this.container.setVisible(false);
        this.isVisible = false;
      },
    });
  }

  /**
   * Toggle panel visibility
   */
  toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if panel is visible
   */
  getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Destroy the panel
   */
  destroy(): void {
    this.toggles.clear();
    this.overlay.destroy();
    this.container.destroy();
  }
}
